import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../lib/redux";
import { ConfirmAlert } from "../components/Alerts/Alerts";
import { useTranslationApp } from "../lib/i18next";
import {
  ANNOTATIONS_MESSAGE_DELETE,
  ANNOTATIONS_MESSAGE_DELETE_CONFIRM,
} from "../i18n/keysTranslations";
import {
  startDeleteAnnotation,
  startListenerAnnotations,
  startManageAnnotation,
  startMarkAnnotationScopeAsRead,
  stopListenerAnnotations,
} from "../actions/annotations";
import { canManageAnnotationMessage } from "../pages/AdminDashboard/utils/annotationMessageActions";
import { resolveInFlightClientIDs } from "../pages/AdminDashboard/utils/annotationPendingSend";
import {
  animateScrollToBottom,
  getScrollBottomPosition,
  isScrollAtBottom,
  scrollElementNearBottom,
} from "../pages/AdminDashboard/utils/annotationThreadScroll";

export const useAnnotationsThread = ({
  target,
  isVisible = true,
  currentOwnerUid,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [annotationsData, setAnnotationsData] = useState(null);
  const [pendingAnnotations, setPendingAnnotations] = useState([]);
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const listenerRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const inFlightSendsRef = useRef(new Map());
  const annotationsDataRef = useRef(null);
  const scrollAnimationCancelRef = useRef(null);
  const previousAnnotationCountRef = useRef(0);
  const shouldAnimateScrollRef = useRef(false);
  const wasAtBottomRef = useRef(true);

  const cancelScrollAnimation = () => {
    scrollAnimationCancelRef.current?.();
    scrollAnimationCancelRef.current = null;
  };

  const advertiserID = target?.advertiserID;
  const isAgency = target?.isAgency;

  const sortedAnnotations = useMemo(() => {
    if (!annotationsData) return [];

    return Object.entries(annotationsData)
      .map(([annotationID, annotation]) => ({
        annotationID,
        ...annotation,
      }))
      .sort(
        (left, right) => (left.creationTime || 0) - (right.creationTime || 0)
      );
  }, [annotationsData]);

  const allAnnotations = useMemo(() => {
    const serverAnnotationIDs = new Set(
      sortedAnnotations.map((annotation) => annotation.annotationID)
    );
    const filteredPending = pendingAnnotations.filter(
      (pending) => !serverAnnotationIDs.has(pending.annotationID)
    );

    return [...sortedAnnotations, ...filteredPending].sort(
      (left, right) => (left.creationTime || 0) - (right.creationTime || 0)
    );
  }, [pendingAnnotations, sortedAnnotations]);

  useEffect(() => {
    annotationsDataRef.current = annotationsData;
  }, [annotationsData]);

  const removePendingByClientID = (clientID) => {
    if (!clientID) return;

    setPendingAnnotations((prev) =>
      prev.filter((item) => item.clientID !== clientID)
    );
    inFlightSendsRef.current.delete(clientID);
  };

  useEffect(() => {
    if (!advertiserID || !isVisible) {
      setAnnotationsData(null);
      return undefined;
    }

    let cancelled = false;

    cancelScrollAnimation();
    previousAnnotationCountRef.current = 0;
    shouldAnimateScrollRef.current = false;
    wasAtBottomRef.current = true;
    inFlightSendsRef.current = new Map();
    setPendingAnnotations([]);
    setAnnotationsData(null);
    setEditingAnnotation(null);

    dispatch(
      startListenerAnnotations({
        advertiserID,
        isAgency,
        onUpdatedDataCallback: (data) => {
          if (cancelled) return;

          const nextData = data ?? {};
          const previousData = annotationsDataRef.current ?? {};
          const clientIDsToResolve = resolveInFlightClientIDs({
            previousData,
            nextData,
            inFlightSends: inFlightSendsRef.current,
          });

          if (clientIDsToResolve.length > 0) {
            const resolvedClientIDs = new Set(clientIDsToResolve);

            setPendingAnnotations((prev) =>
              prev.filter((item) => !resolvedClientIDs.has(item.clientID))
            );
            clientIDsToResolve.forEach((clientID) => {
              inFlightSendsRef.current.delete(clientID);
            });
          }

          setAnnotationsData(nextData);
        },
        onReferenceAvailable: (ref) => {
          listenerRef.current = ref;
        },
      })
    );

    return () => {
      cancelled = true;
      dispatch(stopListenerAnnotations({ listenerRef: listenerRef.current }));
      listenerRef.current = null;
    };
  }, [advertiserID, dispatch, isAgency, isVisible]);

  useEffect(() => {
    if (!isVisible || !advertiserID || !currentOwnerUid) return;

    dispatch(
      startMarkAnnotationScopeAsRead({
        ownerUID: currentOwnerUid,
        advertiserID,
        isAgency,
      })
    );
  }, [advertiserID, currentOwnerUid, dispatch, isAgency, isVisible]);

  useEffect(() => {
    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer || !isVisible) return undefined;

    const onScroll = () => {
      wasAtBottomRef.current = isScrollAtBottom({
        element: scrollContainer,
        thresholdPx: 48,
      });
    };

    scrollContainer.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", onScroll);
    };
  }, [advertiserID, isVisible]);

  useLayoutEffect(() => {
    if (!isVisible || allAnnotations.length === 0) {
      shouldAnimateScrollRef.current = false;
      return;
    }

    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer) return;

    const previousCount = previousAnnotationCountRef.current;
    const currentCount = allAnnotations.length;
    const isThreadInitialLoad = previousCount === 0 && currentCount > 0;
    const isNewMessage = currentCount > previousCount;

    previousAnnotationCountRef.current = currentCount;

    if (!isThreadInitialLoad && !isNewMessage) {
      shouldAnimateScrollRef.current = false;
      return;
    }

    cancelScrollAnimation();

    if (isThreadInitialLoad) {
      shouldAnimateScrollRef.current = true;
      wasAtBottomRef.current = true;
      scrollElementNearBottom({ element: scrollContainer });
      return;
    }

    if (wasAtBottomRef.current) {
      scrollContainer.scrollTop = getScrollBottomPosition(scrollContainer);
      wasAtBottomRef.current = true;
    }

    shouldAnimateScrollRef.current = false;
  }, [allAnnotations.length, isVisible]);

  useEffect(() => {
    if (!isVisible || !shouldAnimateScrollRef.current) return;

    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer) return;

    shouldAnimateScrollRef.current = false;

    const onUserScroll = () => {
      cancelScrollAnimation();
    };

    scrollContainer.addEventListener("wheel", onUserScroll, { passive: true });
    scrollContainer.addEventListener("touchstart", onUserScroll, {
      passive: true,
    });

    animateScrollToBottom({
      element: scrollContainer,
      onCancelRef: scrollAnimationCancelRef,
    });

    return () => {
      scrollContainer.removeEventListener("wheel", onUserScroll);
      scrollContainer.removeEventListener("touchstart", onUserScroll);
      cancelScrollAnimation();
    };
  }, [allAnnotations.length, isVisible]);

  useEffect(() => {
    if (isVisible) return undefined;

    cancelScrollAnimation();
    previousAnnotationCountRef.current = 0;
    shouldAnimateScrollRef.current = false;
    wasAtBottomRef.current = true;

    return undefined;
  }, [isVisible]);

  const onStartEditAnnotation = (annotation) => {
    if (
      !canManageAnnotationMessage({
        annotation,
        currentOwnerUid,
      })
    ) {
      return;
    }

    setEditingAnnotation({
      annotationID: annotation.annotationID,
      text: annotation.text || "",
      files: Array.isArray(annotation.files) ? annotation.files : [],
      mentionedUserIDs: annotation.mentionedUserIDs || [],
    });
  };

  const onCancelEditAnnotation = () => {
    setEditingAnnotation(null);
  };

  const onDeleteAnnotation = async ({ annotationID }) => {
    const annotation = annotationsData?.[annotationID];
    if (
      !canManageAnnotationMessage({
        annotation,
        currentOwnerUid,
      })
    ) {
      return false;
    }

    const result = await ConfirmAlert({
      title: t(ANNOTATIONS_MESSAGE_DELETE),
      text: t(ANNOTATIONS_MESSAGE_DELETE_CONFIRM),
    });

    if (!result?.isConfirmed) return false;

    const previousAnnotation = annotationsData?.[annotationID];

    setAnnotationsData((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[annotationID];
      return next;
    });

    if (editingAnnotation?.annotationID === annotationID) {
      setEditingAnnotation(null);
    }

    const deleteResult = await dispatch(
      startDeleteAnnotation({
        advertiserID: target.advertiserID,
        isAgency: target.isAgency,
        annotationID,
      })
    );

    if (!deleteResult?.ok && previousAnnotation) {
      setAnnotationsData((prev) => ({
        ...(prev ?? {}),
        [annotationID]: previousAnnotation,
      }));
      return false;
    }

    return true;
  };

  const onSendAnnotation = async ({
    text,
    files,
    mentionedUserIDs = [],
    annotationID = null,
  }) => {
    if (!target?.advertiserID || !text?.trim()) return;

    const isEditing = Boolean(annotationID);
    const previousAnnotation = annotationID
      ? annotationsData?.[annotationID]
      : null;
    const clientID = isEditing ? null : crypto.randomUUID();

    if (isEditing) {
      setAnnotationsData((prev) => ({
        ...(prev ?? {}),
        [annotationID]: {
          ...(prev?.[annotationID] ?? {}),
          text: text.trim(),
          files,
          mentionedUserIDs,
          isOptimistic: true,
        },
      }));
    } else {
      const creationTime = Date.now();
      const optimisticAnnotation = {
        annotationID: clientID,
        clientID,
        isOptimistic: true,
        text: text.trim(),
        files,
        mentionedUserIDs,
        userID: currentOwnerUid,
        creationTime,
      };

      inFlightSendsRef.current.set(clientID, {
        text: text.trim(),
        userID: currentOwnerUid,
        creationTime,
      });
      setPendingAnnotations((prev) => [...prev, optimisticAnnotation]);
    }

    setIsSending(true);

    const result = await dispatch(
      startManageAnnotation({
        advertiserID: target.advertiserID,
        text,
        files,
        isAgency: target.isAgency,
        mentionedUserIDs,
        annotationID,
      })
    );

    setIsSending(false);

    if (result?.ok && result?.data?.annotationID) {
      const {
        annotationID: confirmedAnnotationID,
        annotation,
        isEdit,
      } = result.data;

      if (isEdit) {
        setEditingAnnotation(null);

        if (annotation) {
          setAnnotationsData((prev) => ({
            ...(prev ?? {}),
            [confirmedAnnotationID]: annotation,
          }));
        }
      } else {
        removePendingByClientID(clientID);

        if (annotation) {
          setAnnotationsData((prev) => ({
            ...(prev ?? {}),
            [confirmedAnnotationID]: annotation,
          }));
        }
      }

      return;
    }

    if (isEditing && previousAnnotation) {
      setAnnotationsData((prev) => ({
        ...(prev ?? {}),
        [annotationID]: previousAnnotation,
      }));
      return;
    }

    removePendingByClientID(clientID);
  };

  const isAnnotationsLoading =
    Boolean(advertiserID && isVisible) && annotationsData === null;
  const hasNoAnnotations = !isAnnotationsLoading && allAnnotations.length === 0;

  return {
    allAnnotations,
    isAnnotationsLoading,
    hasNoAnnotations,
    editingAnnotation,
    isSending,
    messagesScrollRef,
    onSendAnnotation,
    onStartEditAnnotation,
    onCancelEditAnnotation,
    onDeleteAnnotation,
  };
};
